import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchCompanyProfile } from '../../../services/queries/SourcingCompanyQueries';
import { ISourcingProfileData } from '../data/sourcingProfileMockData';
import { mapCompanyProfileResponse } from '../data/companyProfileAdapter';
import { resolveCompanyLookup } from './resolveCompanyLookup';

export const SOURCING_PROFILE_QUERY_KEY = 'sourcing-profile';

const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * Fetches and adapts the unified company profile for the sourcing profile
 * page. The `companyId` param is whatever is in the route segment — see
 * `resolveCompanyLookup` for how it is translated into an API lookup key.
 */
export function useSourcingProfile(companyId: string) {
  const lookup = useMemo(() => resolveCompanyLookup(companyId), [companyId]);
  const hasLookup = Object.keys(lookup).length > 0;

  return useQuery<ISourcingProfileData>({
    queryKey: [SOURCING_PROFILE_QUERY_KEY, companyId],
    queryFn: async ({ signal }) => {
      const response = await fetchCompanyProfile(lookup, signal);
      return mapCompanyProfileResponse(response, { fallbackId: companyId });
    },
    staleTime: FIVE_MINUTES,
    enabled: Boolean(companyId) && hasLookup,
  });
}
